import React from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { axiosInstance } from "@retro-ia/shared/axios";
import type { StylesConfig } from "react-select";
const customStyles: StylesConfig<ClienteFinalOption, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--background)",
    borderColor: state.isFocused ? "var(--ring)" : "var(--border)",
    boxShadow: state.isFocused ? "0 0 0 2px var(--ring)" : "none",
    borderRadius: "0.5rem",
    minHeight: "2.5rem",
    padding: "0 0.25rem",
    color: "var(--foreground)",
    transition: "border-color 0.2s, box-shadow 0.2s",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--foreground)",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--foreground)",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--background)",
    border: "1px solid var(--muted)",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
    zIndex: 20,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "var(--primary)" : "var(--background)",
    color: state.isFocused ? "var(--muted)" : "var(--foreground)",
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
  }),
  input: (base) => ({
    ...base,
    color: "var(--foreground)",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--muted-foreground)",
  }),
};

export interface SelectClienteFinalProps {
  value?: { value: number; label: string } | null;
  onChange: (option: { value: number; label: string } | null) => void;
  isClearable?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  limit?: number;
  idUsuarioAlta?: number;
}

type ClienteFinalOption = { value: number; label: string };

const fetchOptions = async (
  search: string,
  _loadedOptions: ClienteFinalOption[],
  { page }: { page: number },
  limit: number,
  idUsuarioAlta?: number
): Promise<{
  options: ClienteFinalOption[];
  hasMore: boolean;
  additional: { page: number };
}> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      idUsuarioAlta?: number;
    } = {
      page,
      limit,
      search,
    };
    if (idUsuarioAlta !== undefined) {
      params.idUsuarioAlta = idUsuarioAlta;
    }
    const response = await axiosInstance.get(
      "/clientes-finales/select-options",
      {
        params,
      }
    );
    const { options, total } = response.data;
    return {
      options,
      hasMore: page * limit < total,
      additional: {
        page: page + 1,
      },
    };
  } catch {
    return { options: [], hasMore: false, additional: { page: page + 1 } };
  }
};

export const SelectClienteFinal: React.FC<SelectClienteFinalProps> = ({
  value,
  onChange,
  isClearable = true,
  isDisabled = false,
  placeholder = "Selecciona un cliente final...",
  limit = 10,
  idUsuarioAlta,
}) => {
  // Si el value es solo el id, no el objeto completo, buscar el label
  const [selected, setSelected] = React.useState<{
    value: number;
    label: string;
  } | null>(value ?? null);

  React.useEffect(() => {
    if (value && !value.label) {
      // Si solo tenemos el id, buscar el label desde el backend
      (async () => {
        try {
          const res = await axiosInstance.get(
            `/clientes-finales/${value.value}`
          );
          setSelected({ value: value.value, label: res.data.nombreEmpresa });
        } catch {
          setSelected(null);
        }
      })();
    } else {
      setSelected(value ?? null);
    }
  }, [value]);

  return (
    <AsyncPaginate
      value={selected}
      loadOptions={(inputValue, loadedOptions, additional) =>
        fetchOptions(
          inputValue,
          loadedOptions as ClienteFinalOption[],
          (additional as { page: number }) || { page: 1 },
          limit,
          idUsuarioAlta
        )
      }
      onChange={(option) => {
        setSelected(option);
        onChange(option);
      }}
      isClearable={isClearable}
      isDisabled={isDisabled}
      placeholder={placeholder}
      additional={{ page: 1 }}
      debounceTimeout={300}
      styles={customStyles}
    />
  );
};
